import {AnalyticsService} from '../../../shared/services/analytics/analytics.service';
import {PathUrlEnum} from '../../../utils/analytics.utils';
import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormControl, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {DataPersonalSellerPf} from '../../../shared/models/SellerPf.model';
import {ErrorMessage} from '../../../shared/models/error-message';
import {bancos, tipo_conta} from '../../../utils/constants.utils';
import PatternsUtils from '../../../utils/patterns.utils';
import {mapFormToDataPersonalSellerPfThree} from "../../../utils/seller-pf-mapper.util";
import {patchFormWithNestedObject} from "../../../utils/seller-mapper.utils";

@Component({
  selector: 'app-seller-step-three-pf',
  templateUrl: './seller-step-three-pf.component.html',
  styleUrls: ['./seller-step-three-pf.component.scss'],
})
export class SellerStepThreePfComponent implements OnInit {
  @Output() formsComplete = new EventEmitter();

  @Output() dataForms = new EventEmitter();

  @Input() dataPersonal = new DataPersonalSellerPf();

  formStepThree: FormGroup;

  isFormValid = false;

  listaBancos = [...bancos];
  tipoConta = [...tipo_conta];

  constructor(private analyticsService: AnalyticsService) {
  }

  ngOnInit(): void {
    this.initForm();
    this.getFormData(this.formStepThree, this.dataPersonal);
    this.isFormComplete();
    this.analyticsService.track(PathUrlEnum.PROPRIETARIO_PF_DADOS_PAGAMENTO);
  }

  getErrorMandatory(label: string): string {
    return ErrorMessage.getTextMandatory(label);
  }

  getErrorInvalid(label: string): string {
    return ErrorMessage.getTextInvalid(label);
  }

  initForm() {
    this.formStepThree = new FormGroup({
      payment_bankNumber: new FormControl('', [Validators.required]),
      payment_accountType: new FormControl('', [Validators.required]),
      payment_agencyNumber: new FormControl('', [
        Validators.required,
        Validators.maxLength(4),
        Validators.pattern(PatternsUtils.ONLY_NUMBERS),
      ]),
      payment_accountNumber: new FormControl('', [
        Validators.required,
        Validators.pattern(PatternsUtils.BANK_ACCOUNT_NUMBER_LETTER),
        Validators.minLength(7),
        Validators.maxLength(30)
      ]),

      partner_payment_bankNumber: new FormControl(''),
      partner_payment_agencyNumber: new FormControl(''),
      partner_payment_accountNumber: new FormControl(''),
      partner_payment_accountType: new FormControl(''),
    });

    if (this.isSpouseParticipates()) {
      this.setSpouseParticipatesValidation();
    } else {
      this.clearSpouseParticipatesValidation();
    }
  }

  setSpouseParticipatesValidation() {
    this.isRequired('partner_payment_bankNumber', [Validators.required]);
    this.isRequired('partner_payment_accountType', [Validators.required]);
    this.isRequired('partner_payment_agencyNumber', [
      Validators.required,
      Validators.maxLength(4),
      Validators.pattern(PatternsUtils.ONLY_NUMBERS)
    ]);
    this.isRequired('partner_payment_accountNumber', [
      Validators.required,
      Validators.pattern(PatternsUtils.BANK_ACCOUNT_NUMBER_LETTER),
      Validators.minLength(7),
      Validators.maxLength(30)
    ]);
  }

  clearSpouseParticipatesValidation() {
    this.clearValidation('partner_payment_bankNumber');
    this.clearValidation('partner_payment_accountType');
    this.clearValidation('partner_payment_agencyNumber');
    this.clearValidation('partner_payment_accountNumber');

    this.clearValue('partner_payment_bankNumber');
    this.clearValue('partner_payment_accountType');
    this.clearValue('partner_payment_agencyNumber');
    this.clearValue('partner_payment_accountNumber');
  }

  isSpouseParticipates() {
    return this.dataPersonal.relationship.hasPartInSale;
  }

  isRequired(key: string, validators: ValidatorFn[] | null = null) {
    this.formStepThree.controls[key].setValidators(validators);
    this.formStepThree.controls[key].updateValueAndValidity();
  }

  clearValidation(key: string) {
    this.formStepThree.controls[key].setValidators(null);
    this.formStepThree.controls[key].setErrors(null);
  }

  clearValue(key: string) {
    this.formStepThree.get(key)?.patchValue('');
  }

  formatBankAccount(event: any, partner: boolean) {
    let inputValue = event.target.value.replace(/-/g, '');
    
    // Remove apenas caracteres especiais, mantendo números e letras
    inputValue = inputValue.replace(/[^0-9A-Za-z]/g, '');
    
    let formattedValue = inputValue;
    
    if (inputValue.length > 1) {
      // Separa a conta (parte antes do último caractere) do dígito (último caractere)
      let conta = inputValue.slice(0, -1);
      let digito = inputValue.slice(-1).toUpperCase();
      
      // Verifica se a conta contém apenas números
      const contaTemApenasNumeros = /^[0-9]+$/.test(conta);
      
      // Verifica se a conta não excede 28 caracteres
      const contaNaoExcedeTamanho = conta.length <= 28;
      
      // Verifica se o dígito é válido (letra ou número)
      const digitoValido = /^[0-9A-Z]$/.test(digito);
      
      // Só formata se todas as validações passarem
      if (contaTemApenasNumeros && contaNaoExcedeTamanho && digitoValido && conta.length > 0) {
        formattedValue = `${conta}-${digito}`;
      } else {
        // Mantém o valor original se inválido (campo ficará inválido)
        formattedValue = event.target.value;
      }
    }

    if (partner) {
      this.formStepThree.patchValue({partner_payment_accountNumber: formattedValue});
    } else {
      this.formStepThree.patchValue({payment_accountNumber: formattedValue});
    }
  }

  getFormData(formGroup: FormGroup, data: DataPersonalSellerPf): void {
    patchFormWithNestedObject(formGroup, data);
  }

  isFormComplete() {
    this.formStepThree.statusChanges.subscribe(newStatus => {
      const formValue = this.formStepThree.getRawValue();
      Object.assign(this.dataPersonal, mapFormToDataPersonalSellerPfThree(formValue, this.dataPersonal));
      if (newStatus.toLowerCase() === 'valid') {
        this.formsComplete.emit(true);
        this.dataForms.emit(this.dataPersonal);
      } else {
        this.formsComplete.emit(false);
      }
    });
  }

  formatAgency(event: any, partner: boolean) {
    let agencyNumber = event.target.value.replace(/\D/g, '');
    const maxAgency = 5;
    // adiciona o hífen no quinto último caractere, caso a agência tenha pelo menos 5 caracteres
    if (agencyNumber.length >= maxAgency) {
      agencyNumber = `${agencyNumber.slice(0, -1)}-${agencyNumber.slice(-1)}`;
      if (partner) {
        this.formStepThree.patchValue({partner_payment_agencyNumber: agencyNumber});
      } else {
        this.formStepThree.patchValue({payment_agencyNumber: agencyNumber});
      }
    }
  }
}
